import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import { type PreactPageProps } from "pages/PreactPage";
import * as webReaderInstaller from "webReaderInstaller";

const FIREFOX_ANDROID_URL =
  "https://play.google.com/store/apps/details?id=org.mozilla.firefox";
const IOS_BOOKMARKLET_HELP_URL =
  "https://forum.readlang.com/t/install-the-readlang-web-reader-on-ios-safari/48";

const ExtensionCard = ({
  browser,
  href,
}: {
  browser: string;
  href: string;
}) => (
  <a class="webReaderExtensionCard" href={href} target="_blank" rel="noopener">
    <img src="/icon.png" alt="" />
    <strong>{browser} extension</strong>
    <span class="prominent">Add to {browser}</span>
  </a>
);

const ChromeExtensionCard = () => (
  <ExtensionCard
    browser="Chrome"
    href={webReaderInstaller.CHROME_WEBSTORE_URL}
  />
);

const FirefoxExtensionCard = () => (
  <ExtensionCard
    browser="Firefox"
    href={webReaderInstaller.FIREFOX_ADDON_URL}
  />
);

const DemoMedia = () => (
  <figure class="webReaderDemo">
    <img
      src="/content/web-reader-demo-large.gif"
      alt="The Readlang Web Reader translating words on a web page"
    />
    <figcaption>
      Open any web page in your target language and click a word or phrase to
      translate it instantly.
    </figcaption>
  </figure>
);

const BookmarkletDrag = () => (
  <p class="webReaderBookmarklet">
    <a
      class="bookmarkletLink"
      data-bypass="true"
      href={webReaderInstaller.getBookmarkletLink()}
    >
      📖 Readlang Web Reader
    </a>
    <small>Drag this button up to your bookmarks bar.</small>
  </p>
);

const DesktopGuide = () => (
  <>
    <div class="webReaderExtensions">
      <ChromeExtensionCard />
      <FirefoxExtensionCard />
    </div>

    <DemoMedia />

    <h3>No extension? Use the bookmarklet</h3>
    <p>
      If you can't install an extension you can use the bookmarklet instead.
      It's more fiddly to set up and some websites block it with their security
      policies, so we recommend the extension where possible.
    </p>
    <BookmarkletDrag />

    <h3>The bookmarklet needs third party cookies</h3>
    <p>
      The bookmarklet requires third party cookies (cross-site tracking) to be
      enabled. You don't need to enable them everywhere — just add an exception
      for <code>https://readlang.com</code> in your browser's "Privacy and
      Security" settings. The extension works fine without third party cookies.
    </p>
  </>
);

const AndroidGuide = () => (
  <>
    <p>
      On Android, browser extensions only work in <strong>Firefox</strong>{" "}
      (Chrome for Android doesn't support them). To use the Web Reader on your
      phone:
    </p>
    <ol>
      <li>
        Install <a href={FIREFOX_ANDROID_URL}>Firefox for Android</a>.
      </li>
      <li>Open Firefox and add the Readlang Web Reader extension below.</li>
    </ol>

    <div class="webReaderExtensions">
      <FirefoxExtensionCard />
    </div>

    <DemoMedia />
  </>
);

const IosGuide = () => (
  <>
    <p>
      Readlang doesn't have an iOS Safari extension yet, so the smoothest way to
      use Readlang on iPhone and iPad is to import articles on a computer and
      read them on your phone:
    </p>
    <ol>
      <li>
        On a desktop computer, install the{" "}
        <a href={webReaderInstaller.CHROME_WEBSTORE_URL}>Chrome</a> or{" "}
        <a href={webReaderInstaller.FIREFOX_ADDON_URL}>Firefox</a> extension.
      </li>
      <li>
        Use it to import any article into your{" "}
        <a href="/library">Readlang library</a>.
      </li>
      <li>
        Read the imported articles anytime on readlang.com on your iPhone or
        iPad.
      </li>
    </ol>

    <div class="webReaderExtensions">
      <ChromeExtensionCard />
      <FirefoxExtensionCard />
    </div>

    <DemoMedia />

    <h3>Last resort: the bookmarklet</h3>
    <p>
      The bookmarklet is currently the only way to open the Web Reader directly
      on iOS, but it's fiddly to set up in Safari and is blocked on many sites
      by their security policies, so we only recommend it if importing on a
      computer isn't an option.
    </p>
    <p>
      <a href={IOS_BOOKMARKLET_HELP_URL}>
        How to install the Web Reader bookmarklet on iOS Safari
      </a>
    </p>
  </>
);

export default class WebReaderPageView extends preact.Component<PreactPageProps> {
  render() {
    const device = deviceDetect.info();

    return (
      <div class="webReaderPage">
        <h2 class="spacing">Install the Web Reader</h2>

        <p>
          The Web Reader turns any web page into an interactive lesson — click a
          word or phrase to translate it instantly, then review what you save
          with flashcards.
        </p>

        <p>
          We recommend the browser extension for <strong>Chrome</strong> or{" "}
          <strong>Firefox</strong>. It works on far more websites than the old
          bookmarklet and needs no special browser settings.
        </p>

        {device.android ? (
          <AndroidGuide />
        ) : device.iOS ? (
          <IosGuide />
        ) : (
          <DesktopGuide />
        )}

        <h3>What next?</h3>
        <p>
          Words and phrases you translate are saved automatically. Practice them
          later with flashcards on the <a href="/learn">Flashcards page</a>.
        </p>
        <p>
          For a walkthrough, watch this{" "}
          <a href="https://www.youtube.com/watch?v=qVax6Y_HXmk&index=5&list=PLIGa-eWCssj_dcE9GXkpLSFB9t8qZyZn2">
            video tutorial
          </a>{" "}
          or read the{" "}
          <a href="https://forum.readlang.com/t/install-and-use-the-web-reader/56">
            installation guide on the forum
          </a>
          .
        </p>
      </div>
    );
  }
}
